import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin, LogIn } from "lucide-react";
import { SITE } from "../../lib/sitePoles";
import { useLang, setLang } from "../../lib/siteLang";

const CSS = `
.sp{--navy:#050D24;--navy2:#0A1A3F;--blue:#1E4FA8;--gold:#D9A441;--gold2:#F6D88A;font-family:Manrope,sans-serif;background:linear-gradient(160deg,#0B2A7A 0%,#082063 55%,#06184A 100%) fixed;background-color:#0B2A7A;color:#D3DCF0}
.sp-h{font-family:Michroma,sans-serif;letter-spacing:.04em;color:#fff}
.sp-gold{background:linear-gradient(100deg,#8A5A12 0%,#F6D88A 25%,#C8922E 50%,#FBE6A8 75%,#8A5A12 100%);background-size:250% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:spShine 6s linear infinite}
@keyframes spShine{to{background-position:250% center}}
.sp-btn{background:linear-gradient(135deg,#F6D88A,#C8922E 55%,#8A5A12);color:#050D24;font-weight:800;transition:transform .2s,box-shadow .2s}
.sp-btn:hover{transform:translateY(-2px);box-shadow:0 10px 30px -8px rgba(217,164,65,.6)}
.sp-ghost{border:1px solid rgba(217,164,65,.5);color:#F6D88A;transition:background-color .2s,transform .2s}.sp-ghost:hover{background:rgba(217,164,65,.1);transform:translateY(-2px)}
.sp-card{background:linear-gradient(160deg,rgba(26,75,192,.38),rgba(6,24,74,.55));border:1px solid rgba(217,164,65,.18);backdrop-filter:blur(14px);transition:transform .35s,border-color .35s,box-shadow .35s}
.sp-card:hover{transform:translateY(-6px);border-color:rgba(246,216,138,.6);box-shadow:0 20px 50px -20px rgba(217,164,65,.45)}
.sp-line{height:1px;background:linear-gradient(90deg,transparent,#D9A441,transparent)}
.sp-reveal{opacity:0;transform:translateY(28px);transition:opacity .9s ease,transform .9s ease}.sp-reveal.in{opacity:1;transform:none}
.sp-grain{position:fixed;inset:0;pointer-events:none;z-index:1;opacity:.06;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}
.sp-orbit{animation:spSpin 40s linear infinite;transform-origin:center}@keyframes spSpin{to{transform:rotate(360deg)}}
.sp-float{animation:spFloat 7s ease-in-out infinite}@keyframes spFloat{50%{transform:translateY(-14px)}}
.sp input,.sp textarea,.sp select{background:rgba(6,24,74,.55);border:1px solid rgba(217,164,65,.3);color:#fff;border-radius:.5rem;padding:.7rem .9rem;width:100%;transition:border-color .2s}
.sp input:focus,.sp textarea:focus,.sp select:focus{outline:none;border-color:#F6D88A}
.sp ::selection{background:#D9A441;color:#050D24}
@media (prefers-reduced-motion:reduce){.sp-gold,.sp-orbit,.sp-float{animation:none}.sp-reveal{opacity:1;transform:none}}
`;

export const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const els = ref.current?.querySelectorAll(".sp-reveal") || [];
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
  return ref;
};

const NAV = { fr: [["/accueil#poles", "Nos pôles"], ["/xaltix", "XALTIX"], ["/accueil#apropos", "La société"], ["/contact", "Contact"]],
  en: [["/accueil#poles", "Services"], ["/xaltix", "XALTIX"], ["/accueil#apropos", "About us"], ["/contact", "Contact"]] };
const TX = {
  fr: { login: "Espace client", tagline: "Prestations de services aux entreprises, aux professionnels et aux particuliers, en France et à l'étranger.", legal: "Mentions légales", jobs: "Recrutement", rights: "Tous droits réservés", info: "INFORMATIONS", home: "Prestations de services aux entreprises" },
  en: { login: "Client area", tagline: "Services for companies, professionals and individuals, in France and abroad.", legal: "Legal notice", jobs: "Careers", rights: "All rights reserved", info: "INFORMATION", home: "Business services" },
};

const LangSwitch = ({ testid }) => {
  const L = useLang();
  return (
    <div className="inline-flex rounded-full border border-[#D9A441]/40 p-0.5 text-[11px] font-bold" data-testid={testid}>
      {["fr", "en"].map((l) => <button key={l} type="button" data-testid={`${testid}-${l}`} onClick={() => setLang(l)} aria-pressed={L === l}
        className={`px-2.5 py-1 rounded-full uppercase transition-colors ${L === l ? "bg-[#D9A441] text-[#050D24]" : "text-[#F6D88A] hover:text-white"}`}>{l}</button>)}
    </div>
  );
};

export const SiteShell = ({ children, title }) => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const ref = useReveal();
  const L = useLang();
  const T = TX[L];
  useEffect(() => { document.documentElement.lang = L; }, [L]);
  useEffect(() => {
    if (window.__xtTrack || document.getElementById("xt-track")) return;
    const s = document.createElement("script");
    s.id = "xt-track"; s.async = true; s.src = `${process.env.REACT_APP_BACKEND_URL}/api/public/track.js`;
    document.body.appendChild(s);
  }, []);
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = "https://fonts.googleapis.com/css2?family=Michroma&family=Manrope:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(l);
    return () => l.remove();
  }, []);
  useEffect(() => { document.title = title ? `${title} — Solutions PRIME` : `Solutions PRIME — ${T.home}`; setOpen(false); }, [title, loc.pathname, T.home]);
  useEffect(() => { if (loc.hash) setTimeout(() => document.querySelector(loc.hash)?.scrollIntoView({ behavior: "smooth" }), 80); else window.scrollTo(0, 0); }, [loc.pathname, loc.hash]);
  return (
    <div className="sp min-h-screen relative overflow-x-hidden" ref={ref} data-testid="site-shell">
      <style>{CSS}</style>
      <div className="sp-grain" />
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0B2A7A]/75 border-b border-[#D9A441]/15">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center gap-6">
          <Link to="/accueil" data-testid="site-logo-link" className="flex items-center gap-3">
            <img src="/site/logo-mark.png" alt="Solutions PRIME" className="h-12 w-12 object-contain" />
            <span className="hidden sm:block leading-tight"><span className="block text-[9px] tracking-[0.5em] text-[#F6D88A]">SOLUTIONS</span><span className="sp-h text-lg">PRIME</span></span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8 ml-auto text-sm font-semibold">
            {NAV[L].map(([to, l], i) => <Link key={to} to={to} data-testid={`site-nav-${NAV.fr[i][1]}`} className="hover:text-[#F6D88A] transition-colors">{l}</Link>)}
          </nav>
          <Link to="/login" data-testid="site-login" className="hidden lg:inline-flex sp-ghost items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"><LogIn className="h-4 w-4" />{T.login}</Link>
          <span className="ml-auto lg:ml-0"><LangSwitch testid="site-lang" /></span>
          <button type="button" className="lg:hidden text-[#F6D88A]" data-testid="site-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu">{open ? <X /> : <Menu />}</button>
        </div>
        {open && <nav className="lg:hidden border-t border-[#D9A441]/15 px-5 py-4 flex flex-col gap-4 text-sm font-semibold bg-[#0B2A7A]">
          {NAV[L].map(([to, l], i) => <Link key={to} to={to} data-testid={`site-mnav-${NAV.fr[i][1]}`} onClick={() => setOpen(false)}>{l}</Link>)}
          <Link to="/login" data-testid="site-mnav-login" className="text-[#F6D88A]">{T.login}</Link>
        </nav>}
      </header>
      <main className="relative z-[2] pt-20">{children}</main>
      <footer className="relative z-[2] border-t border-[#D9A441]/15 mt-24" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-3 text-sm">
          <div><img src="/site/logo-256.png" alt="Solutions PRIME" data-testid="site-footer-logo" className="h-32 w-32 object-contain mb-4" /><p className="max-w-xs">{T.tagline}</p></div>
          <div className="space-y-3">
            <div className="sp-h text-xs text-[#F6D88A]">CONTACT</div>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-4 w-4 text-[#D9A441]" />{SITE.email}</a>
            <a href={`tel:${SITE.phoneHref}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4 text-[#D9A441]" />{L === "en" ? "+33 7 63 10 69 73" : SITE.phone}</a>
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#D9A441]" />{SITE.address}{L === "en" ? ", France" : ""}</p>
          </div>
          <div className="space-y-3">
            <div className="sp-h text-xs text-[#F6D88A]">{T.info}</div>
            <Link to="/mentions-legales-solutions-prime" data-testid="site-footer-legal" className="block hover:text-white">{T.legal}</Link>
            <Link to="/postuler" data-testid="site-footer-jobs" className="block hover:text-white">{T.jobs}</Link>
            <Link to="/xaltix" data-testid="site-footer-xaltix" className="block hover:text-white">XALTIX</Link>
          </div>
        </div>
        <div className="sp-line" />
        <p className="text-center text-xs py-6 opacity-70" data-testid="site-copyright">© 2026 SOLUTIONS PRIME — {T.rights}</p>
      </footer>
    </div>
  );
};
